<template>
  <div class="whats-new-page" :class="{ 'whats-new-page-embedded': !standalone }">
    <SiteHeader v-if="standalone"/>

    <section class="wn-hero">
      <v-container class="pt-16 pb-12 text-center">
        <h1 class="wn-hero-title reveal" style="--reveal-index: 0">{{ data && data.title }}</h1>
        <p class="wn-hero-subtitle reveal mx-auto" style="--reveal-index: 1">{{ data && data.subtitle }}</p>
      </v-container>
    </section>

    <section class="wn-news-section" v-if="data">
      <v-container class="pb-16">
        <div
          class="wn-news-item reveal"
          v-for="(item, index) in data.news"
          :key="index"
          :class="{ 'wn-news-item-reverse': index % 2 }"
          :style="{ '--reveal-index': (index % 4) }"
        >
          <div class="wn-news-media">
            <v-img :src="item.img" :alt="item.title" class="wn-news-img" aspect-ratio="1.4" />
          </div>
          <div class="wn-news-content">
            <span class="wn-news-tag">{{ $t(item.type) }}</span>
            <h2 class="wn-news-title">{{ item.title }}</h2>
            <div class="wn-news-text" v-html="item.text"></div>
          </div>
        </div>
      </v-container>
    </section>

    <section class="wn-other-section" v-if="data">
      <v-container class="pb-16">
        <h2 class="section-title text-center mx-auto">{{ $t('otherNews') }}</h2>
        <v-row class="mt-10">
          <v-col cols="12" sm="6" md="4" v-for="(item, index) in data.otherNews" :key="index">
            <a class="wn-other-card reveal" :style="{ '--reveal-index': index }" @click="$router.push(item.src)">
              <v-img :src="item.img" :alt="item.title" height="160" class="wn-other-img"/>
              <div class="wn-other-body">
                <h3 class="wn-other-title">{{ item.title }}</h3>
                <p class="wn-other-text">{{ item.text }}</p>
                <span class="wn-other-cta">
                  {{ $t('accessPainel') }}
                  <v-icon size="16" color="#0277BD">mdi-arrow-top-right-thin</v-icon>
                </span>
              </div>
            </a>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <SiteFooter v-if="standalone"/>
  </div>
</template>

<script>
import SiteHeader from "./fragments/SiteHeader.vue";
import SiteFooter from "./fragments/SiteFooter.vue";

export default {
  name: 'WhatsNew',
  props: [],
  components: {
    SiteHeader,
    SiteFooter,
  },
  data: () => ({
    data: null,
    revealObserver: null,
  }),
  mounted: function() {
    this.data = this.$t('newsMigly')
    this.$nextTick(() => this.setupRevealObserver())
  },
  beforeDestroy: function() {
    if (this.revealObserver) this.revealObserver.disconnect()
  },
  computed: {
    standalone: function() {
      return !this.$route.path.startsWith('/painel')
    },
  },
  methods: {
    setupRevealObserver: function() {
      const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const elements = this.$el.querySelectorAll ? this.$el.querySelectorAll('.reveal') : []
      if (prefersReducedMotion || !window.IntersectionObserver) {
        elements.forEach(el => el.classList.add('is-visible'))
        return
      }
      this.revealObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            this.revealObserver.unobserve(entry.target)
          }
        })
      }, { threshold: 0.15 })
      elements.forEach(el => this.revealObserver.observe(el))
    },
  },
}
</script>
<style scoped>
.whats-new-page {
  font-family: 'Montserrat', sans-serif;
  background: #F7F5EF;
  padding-top: 72px;
}
.whats-new-page-embedded {
  background: transparent;
  padding-top: 0;
}

.reveal {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.6s cubic-bezier(0.16,1,0.3,1), transform 0.6s cubic-bezier(0.16,1,0.3,1);
  transition-delay: calc(var(--reveal-index, 0) * 90ms);
}
.reveal.is-visible {
  opacity: 1;
  transform: translateY(0);
}
@media (prefers-reduced-motion: reduce) {
  .reveal {
    transition: none !important;
    opacity: 1 !important;
    transform: none !important;
  }
}

.wn-hero {
  background: #F7F5EF;
}
.wn-hero-title {
  font-weight: 900;
  font-size: 2.6rem;
  line-height: 1.2;
  color: #0277BD;
  letter-spacing: -0.01em;
}
.wn-hero-subtitle {
  color: #5B6472;
  font-size: 1.05rem;
  max-width: 520px;
  margin-top: 1rem;
}

.wn-news-section {
  background: #F7F5EF;
}
.wn-news-item {
  display: flex;
  align-items: center;
  gap: 2.5rem;
  padding: 2.5rem 0;
  border-bottom: 1px solid rgba(16,24,39,0.08);
}
.wn-news-item:first-child {
  padding-top: 0;
}
.wn-news-item-reverse {
  flex-direction: row-reverse;
}
.wn-news-media {
  flex: 0 0 42%;
}
.wn-news-img {
  border-radius: 1.25rem;
  box-shadow: 0 20px 40px rgba(16,24,39,0.08);
}
.wn-news-content {
  flex: 1;
  min-width: 0;
}
.wn-news-tag {
  display: inline-block;
  background: #E3F1FA;
  color: #0277BD;
  font-weight: 700;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-radius: 999px;
  padding: 0.35rem 0.8rem;
  margin-bottom: 0.9rem;
}
.wn-news-title {
  font-weight: 800;
  font-size: 1.4rem;
  color: #101827;
  margin-bottom: 0.75rem;
  line-height: 1.3;
}
.wn-news-text {
  color: #5B6472;
  font-size: 0.95rem;
  line-height: 1.75;
}
.wn-news-text ::v-deep p {
  margin-bottom: 0.75rem;
}
.wn-news-text ::v-deep h2 {
  font-size: 1.05rem;
  font-weight: 700;
  color: #101827;
  margin: 1rem 0 0.5rem;
}
.wn-news-text ::v-deep ul {
  padding-left: 1.1rem;
  margin-bottom: 0.5rem;
}
.wn-news-text ::v-deep li {
  margin-bottom: 0.4rem;
}
.wn-news-text ::v-deep strong {
  color: #101827;
}
.wn-news-text ::v-deep a {
  color: #0277BD;
  font-weight: 600;
}

.wn-other-section {
  background: #FFFFFF;
}
.section-title {
  font-weight: 800;
  font-size: 2.1rem;
  color: #0277BD;
  text-transform: lowercase;
  max-width: 720px;
}
.wn-other-card {
  display: block;
  background: #FFFFFF;
  border: 1px solid rgba(16,24,39,0.08);
  border-radius: 1.25rem;
  overflow: hidden;
  height: 100%;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.wn-other-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(16,24,39,0.08);
}
.wn-other-img {
  border-radius: 0;
}
.wn-other-body {
  padding: 1.5rem;
}
.wn-other-title {
  font-weight: 700;
  font-size: 1.1rem;
  color: #101827;
  margin-bottom: 0.5rem;
}
.wn-other-text {
  color: #5B6472;
  font-size: 0.88rem;
  line-height: 1.6;
  margin-bottom: 0.9rem;
}
.wn-other-cta {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #0277BD;
  font-weight: 700;
  font-size: 0.85rem;
}

@media (max-width: 960px) {
  .wn-hero-title {
    font-size: 2rem;
  }
  .wn-news-item,
  .wn-news-item-reverse {
    flex-direction: column;
    align-items: stretch;
  }
  .wn-news-media {
    flex: none;
  }
}
</style>
